import { useRef, useMemo, useState } from 'react';
import { Modal, Form, Select, Radio, Button as AntButton } from 'antd';
import { CommonFormProps } from '.';
import { analytics } from 'src/services/analytics';
import { getAppointmentDetailsForAnalytics } from 'src/utils';
import { useTranslation } from 'react-i18next';
import { useConfigStore } from 'src/hooks/settings';

const FORM_TYPE = 'NDI/ODI';
const NDI_FORM_TYPE = 'NDI';
const ODI_FORM_TYPE = 'ODI';

type Props = CommonFormProps;

function PainManagementForm({ isOpen, form, onSave, appointment }: Props) {
  const [painType, setPainType] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [painTypeForm] = Form.useForm();
  const [ndiForm] = Form.useForm<Record<string, number>>();
  const [odiForm] = Form.useForm<Record<string, number>>();
  const [currentFormType, setCurrentFormType] = useState<'ODI' | 'NDI' | null>(
    null
  );

  const appointmentDetails = getAppointmentDetailsForAnalytics(appointment);

  const finalFormResponse = useRef<
    Partial<
      Record<
        typeof NDI_FORM_TYPE | typeof ODI_FORM_TYPE,
        {
          response: any;
          score: number;
          percentage: number;
          max_score: number;
        }
      >
    >
  >({});

  const { config } = useConfigStore()!;

  const forms = (form.content as { forms: { NDI: any; ODI: any } })?.forms;

  const { t } = useTranslation('forms', { keyPrefix: 'painManagement' });

  const formTypeOptions = useMemo(() => {
    const options = [
      { value: 'neck', label: t('painOptions.neck') },
      { value: 'lower_back', label: t('painOptions.lowerBack') },
      { value: 'both', label: t('painOptions.both') },
    ];

    if (config['ODI/NDI']?.allow_none_selection) {
      options.push({ value: 'none', label: t('painOptions.none') });
    }
    return options;
  }, [t]);

  const handleFormTypeChange = (value: string) => {
    analytics.track('Picked Pain Type In NDI/ODI Form', {
      painType: value,
      ...appointmentDetails,
    });
    setPainType(value);
  };

  const handleNDISubmit = async () => {
    analytics.track('Clicked Submit In NDI Form', {
      painType,
      ...appointmentDetails,
    });
    const values = await ndiForm.validateFields();
    const score = Object.entries(values).reduce((acc, [key, value]) => {
      acc = acc + value;
      return acc;
    }, 0);

    const questions: any[] = forms.NDI.questions;
    const response = questions.map(({ title, options }) => ({
      title,
      score: values[title],
      selected_option: options.find(
        (option: any) => option.value === values[title]
      ),
    }));

    finalFormResponse.current[NDI_FORM_TYPE] = {
      response,
      score,
      percentage: score * 2,
      max_score: questions.length * 5,
    };
  };

  const handleODISubmit = async () => {
    analytics.track('Clicked Submit In ODI Form', {
      painType,
      ...appointmentDetails,
    });
    const values = await odiForm.validateFields();
    const score = Object.entries(values).reduce((acc, [key, value]) => {
      acc = acc + value;
      return acc;
    }, 0);

    const questions: any[] = forms.ODI.questions;
    const response = questions.map(({ title, options }) => ({
      title,
      score: values[title],
      selected_option: options.find(
        (option: any) => option.value == values[title]
      ),
    }));

    finalFormResponse.current[ODI_FORM_TYPE] = {
      response,
      score,
      percentage: score * 2,
      max_score: questions.length * 5,
    };
  };

  const submit = async () => {
    setLoading(true);
    let isDone = false;

    try {
      if (!currentFormType) {
        const values = await painTypeForm.validateFields();
        if (values.painType === 'none') {
          isDone = true;
        } else if (values.painType === 'lower_back') {
          setCurrentFormType('ODI');
        } else {
          setCurrentFormType('NDI');
        }
      } else if (currentFormType === 'NDI') {
        await handleNDISubmit();
        isDone = true;
        if (painType !== 'neck') {
          isDone = false;
          setCurrentFormType('ODI');
        }
      } else {
        await handleODISubmit();
        isDone = true;
      }

      if (isDone) {
        /**
         * @todo: Harshit | passThroughActions is not defined
         */
        await onSave({
          form_type: FORM_TYPE,
          response: finalFormResponse.current,
        });
      }
    } catch {
      // intentionally not doing anything. The form already has validation
    } finally {
      setLoading(false);
    }
  };

  const getForm = () => {
    if (currentFormType === null) {
      return {
        title: t('title'),
        body: (
          <Form form={painTypeForm} key={'questionnaireForm'} layout="vertical">
            <Form.Item
              name="painType"
              rules={[
                {
                  required: true,
                  message: t('validation.required'),
                },
              ]}
            >
              <Select value={painType} onChange={handleFormTypeChange}>
                {formTypeOptions.map((option) => (
                  <Select.Option key={option.value} value={option.value}>
                    {option.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Form>
        ),
      };
    }

    const renderRadioField = (item: any, index: number) => {
      return (
        <Form.Item
          key={index}
          name={item.title}
          label={<div className="text-md font-medium">{item.title}</div>}
          rules={[{ required: true, message: t('validation.required') }]}
        >
          <Radio.Group
            options={item.options.map((option: any) => ({
              ...option,
              style: { display: 'flex' },
            }))}
          />
        </Form.Item>
      );
    };

    const ndiFormData = forms.NDI;
    if (currentFormType === 'NDI') {
      return {
        title: ndiFormData.title,
        description: ndiFormData.description,
        body: (
          <Form key={'NDI'} layout="vertical" form={ndiForm}>
            {ndiFormData.questions?.map(renderRadioField)}
          </Form>
        ),
      };
    }

    const odiFormData = forms.ODI;
    return {
      title: odiFormData.title,
      description: odiFormData.description,
      body: (
        <Form key={'ODI'} layout="vertical" form={odiForm}>
          {odiFormData.questions?.map(renderRadioField)}
        </Form>
      ),
    };
  };

  const { title, description, body } = getForm();

  return (
    <Modal
      open={isOpen}
      title={
        <div>
          <div className="text-xl font-bold">{title}</div>
          <div className="text-sm font-normal text-gray-500">{description}</div>
        </div>
      }
      footer={
        <div className="flex justify-end">
          <AntButton
            className="ih-button-primary"
            onClick={submit}
            loading={!!(painType && loading)}
            disabled={loading}
          >
            {t('submit')}
          </AntButton>
        </div>
      }
      closable={false}
      centered={true}
    >
      <div className="flex flex-col gap-4">{body}</div>
    </Modal>
  );
}

export { PainManagementForm };
